import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { 
  TrendingUp, DollarSign, Users, FileText, AlertCircle,
  PieChart, BarChart3, Activity
} from "lucide-react";
import { NestIcon } from "../../components/NestIcon";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentAnalytics() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [claimsByStatus, setClaimsByStatus] = useState([]);
  const [quotesByStatus, setQuotesByStatus] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const [overviewRes, claimsRes, quotesRes] = await Promise.all([
        axios.get(`${API}/agent/analytics/overview`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/analytics/claims-by-status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/analytics/quotes-by-status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setAnalytics(overviewRes.data);
      setClaimsByStatus(claimsRes.data);
      setQuotesByStatus(quotesRes.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500",
      approved: "bg-green-500",
      rejected: "bg-red-500",
      active: "bg-blue-500",
      under_review: "bg-purple-500",
      paid: "bg-emerald-500",
      converted: "bg-indigo-500",
      expired: "bg-gray-500"
    };
    return colors[status] || "bg-gray-400";
  };

  if (loading) {
    return (
      <AgentLayout title="Analytics">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="Analytics">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
          Analytics Dashboard
        </h1>
        <p className="text-muted-foreground">Comprehensive business insights and metrics</p>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Total Revenue</span>
            </div>
            <p className="text-3xl font-bold text-emerald-800">
              ${analytics?.revenue?.total_revenue?.toLocaleString() || 0}
            </p>
            <p className="text-xs text-emerald-600 mt-1">From all policies</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Avg Premium</span>
            </div>
            <p className="text-3xl font-bold text-blue-800">
              ${analytics?.revenue?.average_premium?.toFixed(2) || 0}
            </p>
            <p className="text-xs text-blue-600 mt-1">Per policy/month</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-700">Total Claim Payouts</span>
            </div>
            <p className="text-3xl font-bold text-purple-800">
              ${analytics?.claims?.total_claim_payouts?.toLocaleString() || 0}
            </p>
            <p className="text-xs text-purple-600 mt-1">All time</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-5 w-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-700">Avg Rent Covered</span>
            </div>
            <p className="text-3xl font-bold text-amber-800">
              ${analytics?.revenue?.average_rent?.toFixed(0) || 0}
            </p>
            <p className="text-xs text-amber-600 mt-1">Per policy</p>
          </CardContent>
        </Card>
      </div>

      {/* User & Policy Metrics */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <Card className="card-organic">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Young Serif, serif' }}>
              <Users className="h-5 w-5 text-blue-600" />
              User Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg text-center">
                <p className="text-3xl font-bold text-foreground">{analytics?.users?.total || 0}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg text-center">
                <p className="text-3xl font-bold text-blue-600">{analytics?.users?.new_this_month || 0}</p>
                <p className="text-sm text-muted-foreground">New (30 days)</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg text-center">
                <p className="text-3xl font-bold text-green-600">{analytics?.users?.with_active_policy || 0}</p>
                <p className="text-sm text-muted-foreground">With Active Policy</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg text-center">
                <p className="text-3xl font-bold text-amber-600">{analytics?.users?.with_claims || 0}</p>
                <p className="text-sm text-muted-foreground">With Claims</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-organic">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Young Serif, serif' }}>
              <NestIcon className="h-5 w-5 text-green-600" />
              Policy Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg text-center">
                <p className="text-3xl font-bold text-foreground">{analytics?.policies?.total || 0}</p>
                <p className="text-sm text-muted-foreground">Total Policies</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg text-center">
                <p className="text-3xl font-bold text-green-600">{analytics?.policies?.active || 0}</p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg text-center">
                <p className="text-3xl font-bold text-amber-600">{analytics?.policies?.expiring_soon || 0}</p>
                <p className="text-sm text-muted-foreground">Expiring (30 days)</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg text-center">
                <p className="text-3xl font-bold text-red-600">{analytics?.policies?.expired || 0}</p>
                <p className="text-sm text-muted-foreground">Expired</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Claims & Quotes Breakdown */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="card-organic">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Young Serif, serif' }}>
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Claims by Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {claimsByStatus.length > 0 ? claimsByStatus.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(item.status || item._id)}`} />
                    <span className="capitalize font-medium">{(item.status || item._id)?.replace('_', ' ') || 'Unknown'}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant="secondary">{item.count}</Badge>
                    <div className="w-24 bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${getStatusColor(item.status || item._id)}`}
                        style={{ width: `${Math.min((item.count / (analytics?.claims?.total || 1)) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              )) : (
                <p className="text-center text-muted-foreground py-4">No claims data</p>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Claims</span>
                <span className="font-bold">{analytics?.claims?.total || 0}</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-muted-foreground">Approval Rate</span>
                <span className="font-bold text-green-600">
                  {analytics?.claims?.approval_rate?.toFixed(1) || 0}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-organic">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Young Serif, serif' }}>
              <FileText className="h-5 w-5 text-blue-600" />
              Quotes by Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {quotesByStatus.length > 0 ? quotesByStatus.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(item.status || item._id)}`} />
                    <span className="capitalize font-medium">{(item.status || item._id)?.replace('_', ' ') || 'Unknown'}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant="secondary">{item.count}</Badge>
                    <div className="w-24 bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${getStatusColor(item.status || item._id)}`}
                        style={{ width: `${Math.min((item.count / (analytics?.quotes?.total || 1)) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              )) : (
                <p className="text-center text-muted-foreground py-4">No quotes data</p>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Quotes</span>
                <span className="font-bold">{analytics?.quotes?.total || 0}</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-muted-foreground">Conversion Rate</span>
                <span className="font-bold text-blue-600">
                  {analytics?.quotes?.conversion_rate?.toFixed(1) || 0}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AgentLayout>
  );
}
